<template>
  <div v-bind:class="{ hide: noUi }">
    <div class="song-container">
      <div
        class="waveform"
        v-bind:class="{ hidden: !song || error || loading }"
        v-bind:id="waveform_container_id"
        @click.stop=""
      ></div>
      <div v-if="loading && !error" class="loading-wave" id="new_song_loading">
        <lottie-animation path="remix-just-waves.json" :loop="true" />
      </div>
      <div v-if="error" class="error-text pt-4 pb-4" id="new_song_error">
        <div id="error_message" class="text-danger">
          Network error. Check your connection and reload.
          <div class="mt-3">
            <Button v-on:click.prevent="retryLoad" variant="secondary">
              reload
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WaveSurfer from "wavesurfer.js";
import { mapActions } from "vuex";
import { throttle } from "lodash";
import { isMobile } from "@/utils";

export default {
  name: "WaveformPlayer",
  data() {
    return {
      waveform_container_id:
        "randid" +
        Math.random()
          .toString(36)
          .substring(7),
      initialized: false,
      loading: false,
      error: false,
      ac: null,
      duration: 0,
      elapsed: 0,
      trackedPlay: false,
      localPlayState: false
    };
  },
  props: {
    song: {},
    emptyMessage: String,
    noUi: Boolean,
    autoPlay: Boolean,
    loadingMessage: String,
    forceNames: Boolean,
    forceInstrumental: {
      type: Boolean,
      default: false
    },
    interact: {
      type: Boolean,
      default: true
    },
    globalPlayer: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    if (this.song) {
      this.initialize();
    }
  },
  watch: {
    song(newSong, oldSong) {
      if (this.initialized === false && newSong) {
        this.initialize();
      }
      if (
        this.initialized &&
        newSong &&
        (!oldSong || newSong.id != oldSong.id)
      ) {
        this.elapsed = 0;
        this.trackedPlay = false;
        if (
          !oldSong ||
          newSong.mp3File !== oldSong.mp3File ||
          newSong.mergedMp3File !== oldSong.mergedMp3File
        ) {
          this.loadSong(newSong);
        } else if (newSong.id != oldSong.id) {
          this.wavesurfer.seekTo(0);
        }
      }
      if (!newSong && this.initialized) {
        this.wavesurfer.stop();
      }
    },
    activeSongId(newId) {
      if (newId && this.song && this.song.id !== newId && this.playing) {
        this.wavesurfer.pause();
        this.setPlayState(false);
      }
    },
    autoPlay(value) {
      if (value && this.initialized && !this.loading) {
        try {
          this.wavesurfer.play();
          this.setPlayState(true);
        } catch (e) {
          this.setPlayState(false);
        }
      } else if (!value && this.playing) {
        try {
          this.wavesurfer.stop();
          this.setPlayState(false);
        } catch (e) {
          this.setPlayState(true);
        }
      }
    },
    playing(value) {
      if (value && this.initialized && !this.loading) {
        try {
          this.wavesurfer.play();
          this.setPlayState(true);
        } catch (e) {
          this.setPlayState(false);
        }
      } else {
        try {
          this.wavesurfer.pause();
          this.setPlayState(false);
        } catch (e) {
          this.setPlayState(true);
        }
      }
    }
  },
  computed: {
    playing() {
      return this.globalPlayer
        ? this.$store.getters["player/isPlaying"]
        : this.localPlayState;
    },
    activeSongId() {
      return this.$store.getters["player/getActiveSongId"];
    },
    wavesurferOpts() {
      return {
        waveColor: "rgba(26, 22, 50, 0.6)",
        progressColor: "#21ECD8",
        cursorColor: "#21ECD8",
        height: 74,
        responsive: true,
        normalize: true,
        barWidth: 4,
        barMinHeight: 5,
        barGap: 4,
        barRadius: 4,
        cursorWidth: 0,
        interact: this.interact,
        backend: "MediaElement"
      };
    }
  },
  methods: {
    ...mapActions("player", ["setDuration", "setElapsed", "setActiveSongId"]),
    throttleSetElapsed: throttle(function(elapsed) {
      this.setElapsed(elapsed);
    }, 100),
    initialize() {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.ac = new AudioContext();
      const wavesurferOptions = {
        container: "#" + this.waveform_container_id + "",
        ...this.wavesurferOpts,
        audioContext: this.ac || null
      };
      this.wavesurfer = WaveSurfer.create(wavesurferOptions);
      this.wavesurfer.on("waveform-ready", this.setLoaded);
      this.wavesurfer.on("error", this.handleLoadError);
      this.wavesurfer.on("seek", this.handleSeek);
      this.wavesurfer.on("finish", this.handleStop);
      this.wavesurfer.on("audioprocess", this.handleAudioProcess);
      this.initialized = true;
      if (this.song) {
        this.loadSong(this.song);
      }
    },
    loadSong(song) {
      if (song && (song.mp3File || song.mergedMp3File)) {
        this.setPlayState(false);
        this.loading = true;
        let url = "";
        if (song.mergedMp3File && !this.forceInstrumental) {
          url = song.mergedMp3File;
        } else {
          url = song.mp3File;
        }
        if (url) {
          const pattern = /^((http|https|):\/\/)/;
          if (!pattern.test(url)) {
            url = process.env.VUE_APP_SONG_URL_PREFIX + url;
          }
          this.wavesurfer.load(url);
        }
      }
    },
    setLoaded() {
      this.loading = false;
      const duration = this.wavesurfer.getDuration();
      this.duration = duration;
      this.setDuration(duration);
      if (this.autoPlay) {
        try {
          this.wavesurfer.play();
          this.setPlayState(true);
        } catch (e) {
          this.setPlayState(false);
        }
      }
    },
    playSong() {
      if (this.wavesurfer) {
        if (!this.playing && this.song && this.song.id !== this.activeSongId) {
          this.setActiveSongId(this.song.id);
        }
        const playPromise = this.wavesurfer.playPause();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              this.setPlayState(this.wavesurfer.isPlaying());
            })
            .catch(() => {
              this.setPlayState(this.wavesurfer.isPlaying());
              // console.log(error);
            });
        } else {
          this.setPlayState(this.wavesurfer.isPlaying());
        }
      }
    },
    setPlayState(playState) {
      if (this.globalPlayer) {
        this.$store.dispatch("player/setPlayState", playState);
      } else {
        this.localPlayState = playState;
      }
    },
    handleLoadError(error) {
      if (error === "fetch error: Failed to fetch") {
        this.error = true;
      }
    },
    handleStop() {
      this.setPlayState(false);
    },
    handleAudioProcess(seconds) {
      this.elapsed = seconds;
      this.throttleSetElapsed(seconds);
      if (seconds >= 5 && !this.trackedPlay) {
        if (this.$amplitude) {
          this.$amplitude.getInstance().logEvent("song_play", {
            song_id: this.activeSongId
          });
        }
        this.trackedPlay = true;
      }
    },
    handleSeek(percentage) {
      const elapsed = percentage * this.duration;
      this.elapsed = elapsed;
      this.throttleSetElapsed(elapsed);
    },
    retryLoad() {
      this.error = false;
      this.loadSong(this.song);
    }
  },
  beforeDestroy() {
    if (this.wavesurfer) {
      this.wavesurfer.un("ready", this.setLoaded);
      this.wavesurfer.un("error", this.handleLoadError);
      this.wavesurfer.un("seek", this.handleSeek);
      this.wavesurfer.un("finish", this.handleStop);
      this.wavesurfer.un("audioprocess", this.handleAudioProcess);
      if (this.initialized) {
        this.wavesurfer.stop();
        if (this.ac) {
          this.ac.close();
        }
        delete this.wavesurfer.backend.buffer;
        this.wavesurfer.destroy();
        this.initialized = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.waveform {
  height: 74px;
  &.hidden {
    opacity: 0;
    margin-top: -74px;
  }
}

.song-container {
  padding: 10px 14px 14px;
  position: relative;
}

.loading-wave {
  height: 74px;
}

.error-text {
  margin-left: -72px;
}
</style>
